import React, { useState, useEffect } from 'react';
import { modelRegistryAPI } from '../api';
import { InfoTooltip, LoadingState, ErrorState, EmptyState, ConfirmDialog, InlineNotice } from './shared/UIComponents';

const TrashView = () => {
  const [deletedModels, setDeletedModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDataset, setFilterDataset] = useState('all');
  const [filterModelType, setFilterModelType] = useState('all');
  const [sortBy, setSortBy] = useState('deleted_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadDeletedModels();
  }, []);

  const loadDeletedModels = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch full list including soft-deleted models
      const list = await modelRegistryAPI.listSavedModels(undefined, undefined, true);
      // Filter for deleted models (those with deletedAt/deleted_at timestamp)
      const deleted = (list || []).filter(model => {
        const ts = model.deletedAt ?? model.deleted_at ?? null;
        return ts !== null && ts !== undefined;
      });
      setDeletedModels(deleted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreModel = async (modelId) => {
    try {
      setActionLoading(true);
      await modelRegistryAPI.restoreModel(modelId);
      await loadDeletedModels();
      setNotice({ type: 'success', message: 'Model restored successfully.' });
    } catch (err) {
      setNotice({ type: 'error', message: `Restore failed: ${err.message}` });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleHardDeleteModel = async (modelId) => {
    try {
      setActionLoading(true);
      await modelRegistryAPI.hardDeleteModel(modelId);
      await loadDeletedModels();
      setNotice({ type: 'success', message: 'Model permanently deleted.' });
    } catch (err) {
      setNotice({ type: 'error', message: `Permanent delete failed: ${err.message}` });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleEmptyTrash = async () => {
    try {
      setActionLoading(true);
      const deletePromises = deletedModels.map(model => 
        modelRegistryAPI.hardDeleteModel(model.id).catch(err => {
          console.error(`Failed to delete model ${model.id}:`, err);
          return { success: false, error: err.message };
        })
      );
      
      const results = await Promise.all(deletePromises);
      const successful = results.filter(r => r.success).length;
      const failed = results.length - successful;
      
      await loadDeletedModels();
      
      if (failed > 0) {
        setNotice({ type: 'error', message: `Deleted ${successful} models. ${failed} models failed to delete.` });
      } else {
        setNotice({ type: 'success', message: `Successfully deleted ${successful} models.` });
      }
    } catch (err) {
      setNotice({ type: 'error', message: `Empty trash failed: ${err.message}` });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  // Filter and sort models
  const filteredAndSortedModels = deletedModels.filter(model => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      model.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.dataset_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.model_type.toLowerCase().includes(searchTerm.toLowerCase());

    // Dataset filter
    const matchesDataset = filterDataset === 'all' || model.dataset_name === filterDataset;

    // Model type filter
    const matchesModelType = filterModelType === 'all' || model.model_type === filterModelType;

    return matchesSearch && matchesDataset && matchesModelType;
  }).sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Handle nested values
    if (sortBy === 'accuracy' && a.metrics) {
      aValue = a.metrics.accuracy || 0;
      bValue = b.metrics.accuracy || 0;
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  // Pagination calculations
  const totalModels = filteredAndSortedModels.length;
  const totalPages = Math.ceil(totalModels / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentModels = filteredAndSortedModels.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Handle pagination navigation
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Get unique datasets and model types for filters
  const uniqueDatasets = [...new Set(deletedModels.map(m => m.dataset_name))];
  const uniqueModelTypes = [...new Set(deletedModels.map(m => m.model_type))];

  const formatFileSize = (sizeMb) => {
    if (sizeMb == null || isNaN(sizeMb)) return 'N/A';
    if (sizeMb < 1) return `${(sizeMb * 1024).toFixed(1)} KB`;
    return `${Number(sizeMb).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatTimeSince = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  if (loading) return <LoadingState message="Loading deleted models..." />;
  if (error) return <ErrorState message={error} onRetry={loadDeletedModels} />;

  return (
    <div className="trash-view">
      <div className="trash-view__header">
        <h2 className="trash-view__title">
          <svg 
            width="24" 
            height="24" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            style={{ marginRight: '8px', verticalAlign: 'middle' }}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
            />
          </svg>
          Model Trash
        </h2>
        <InfoTooltip 
          title="Model Trash" 
          content="View and manage soft-deleted models. You can restore them or permanently delete them." 
        />
      </div>
      <InlineNotice type={notice?.type} message={notice?.message} />

      {/* Statistics */}
      <div className="trash-view__stats">
        <div className="trash-view__stats-grid">
          <div className="trash-view__stats-item">
            <span className="trash-view__stats-label">Deleted Models:</span>
            <div className="trash-view__stats-value">{deletedModels.length}</div>
          </div>
          <div className="trash-view__stats-item">
            <span className="trash-view__stats-label">Total Size:</span>
            <div className="trash-view__stats-value">
              {formatFileSize(deletedModels.reduce((sum, model) => sum + (model.file_size_mb || 0), 0))}
            </div>
          </div>
          <div className="trash-view__stats-item">
            <span className="trash-view__stats-label">Datasets:</span>
            <div className="trash-view__stats-value">{uniqueDatasets.length}</div>
          </div>
          <div className="trash-view__stats-item">
            <span className="trash-view__stats-label">Model Types:</span>
            <div className="trash-view__stats-value">{uniqueModelTypes.length}</div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="trash-view__actions">
        <button
          onClick={() => setConfirmAction({ type: 'empty' })}
          disabled={actionLoading || deletedModels.length === 0}
          className="btn btn--danger"
          title="Permanently delete all models in trash"
        >
          {actionLoading ? 'Deleting...' : 'Empty Trash'}
        </button>
        <button
          onClick={loadDeletedModels}
          className="btn btn--outline"
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="trash-view__filters">
        <div className="trash-view__filters-grid">
          <input
            type="text"
            placeholder="Search deleted models..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              resetPagination();
            }}
            className="trash-view__filter-input"
          />
          <select
            value={filterDataset}
            onChange={(e) => {
              setFilterDataset(e.target.value);
              resetPagination();
            }}
            className="trash-view__filter-select"
          >
            <option value="all">All Datasets</option>
            {uniqueDatasets.map(dataset => (
              <option key={dataset} value={dataset}>{dataset}</option>
            ))}
          </select>
          <select
            value={filterModelType}
            onChange={(e) => {
              setFilterModelType(e.target.value);
              resetPagination();
            }}
            className="trash-view__filter-select"
          >
            <option value="all">All Model Types</option>
            {uniqueModelTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
            className="trash-view__filter-select"
          >
            <option value="deleted_at-desc">Most Recently Deleted</option>
            <option value="deleted_at-asc">Least Recently Deleted</option>
            <option value="created_at-desc">Newest Created</option>
            <option value="created_at-asc">Oldest Created</option>
            <option value="id-asc">ID A-Z</option>
            <option value="id-desc">ID Z-A</option>
            <option value="dataset_name-asc">Dataset A-Z</option>
            <option value="file_size_mb-desc">Largest First</option>
            <option value="accuracy-desc">Highest Accuracy</option>
          </select>
        </div>
      </div>

      {/* Models Table */}
      {totalModels === 0 ? (
        <EmptyState 
          title="No deleted models found"
          description="Deleted models will appear here. You can restore them or permanently delete them."
        />
      ) : (
        <>
          <div className="trash-view__table-container">
            <table className="trash-view__table">
              <thead>
                <tr className="trash-view__table-header">
                  <th className="trash-view__table-header-cell">Model ID</th>
                  <th className="trash-view__table-header-cell">Dataset</th>
                  <th className="trash-view__table-header-cell">Type</th>
                  <th className="trash-view__table-header-cell">Deleted</th>
                  <th className="trash-view__table-header-cell">Created</th>
                  <th className="trash-view__table-header-cell">Size</th>
                  <th className="trash-view__table-header-cell">Accuracy</th>
                  <th className="trash-view__table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentModels.map((model) => (
                  <tr key={model.id} className="trash-view__table-row">
                    <td className="trash-view__table-cell trash-view__table-cell--id">{model.id}</td>
                    <td className="trash-view__table-cell">{model.dataset_name}</td>
                    <td className="trash-view__table-cell">
                      <span className="trash-view__model-type">
                        {model.model_type}
                      </span>
                    </td>
                    <td className="trash-view__table-cell trash-view__table-cell--date">
                      <div>
                        <div>{formatDate(model.deleted_at)}</div>
                        <div style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>
                          {formatTimeSince(model.deleted_at)}
                        </div>
                      </div>
                    </td>
                    <td className="trash-view__table-cell trash-view__table-cell--date">{formatDate(model.created_at)}</td>
                    <td className="trash-view__table-cell trash-view__table-cell--size">{formatFileSize(model.file_size_mb || 0)}</td>
                    <td className="trash-view__table-cell trash-view__table-cell--accuracy">
                      {model.metrics?.accuracy ? 
                        `${(model.metrics.accuracy * 100).toFixed(1)}%` : 
                        model.metrics?.r2_score ? 
                        `R²: ${model.metrics.r2_score.toFixed(3)}` : 
                        'N/A'
                      }
                    </td>
                    <td className="trash-view__table-cell">
                      <div className="trash-view__table-actions">
                        <button
                          onClick={() => setConfirmAction({ type: 'restore', modelId: model.id })}
                          className="trash-view__action-btn trash-view__action-btn--restore"
                          title="Restore Model"
                          disabled={actionLoading}
                        >
                          <svg 
                            width="16" 
                            height="16" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth="2" 
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                            />
                          </svg>
                          Restore
                        </button>
                        <button
                          onClick={() => setConfirmAction({ type: 'hardDelete', modelId: model.id })}
                          className="trash-view__action-btn trash-view__action-btn--delete"
                          title="Permanently Delete"
                          disabled={actionLoading}
                        >
                          <svg 
                            width="16" 
                            height="16" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth="2" 
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                            />
                          </svg>
                          Delete Forever
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="trash-view__pagination">
              <div className="trash-view__pagination-info">
                Showing {startIndex + 1}-{Math.min(endIndex, totalModels)} of {totalModels} deleted models
                {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
              </div>
              
              <div className="trash-view__pagination-controls">
                {/* Previous button */}
                <button
                  onClick={goToPrevious}
                  disabled={currentPage === 1}
                  className={`trash-view__pagination-btn ${
                    currentPage === 1
                      ? 'trash-view__pagination-btn--disabled'
                      : 'trash-view__pagination-btn--prev'
                  }`}
                >
                  Previous
                </button>
                
                {/* Page numbers */}
                <div className="trash-view__pagination-pages">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    // Adjust start if we're near the end
                    if (endPage - startPage < maxVisiblePages - 1) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }
                    
                    // Show first page if not in range
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => goToPage(1)}
                          className="trash-view__pagination-page"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="start-ellipsis" className="trash-view__pagination-ellipsis">...</span>
                        );
                      }
                    }
                    
                    // Show page range
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => goToPage(i)}
                          className={`trash-view__pagination-page ${
                            currentPage === i
                              ? 'trash-view__pagination-page--active'
                              : ''
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // Show last page if not in range
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="end-ellipsis" className="trash-view__pagination-ellipsis">...</span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => goToPage(totalPages)}
                          className="trash-view__pagination-page"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                
                {/* Next button */}
                <button
                  onClick={goToNext}
                  disabled={currentPage === totalPages}
                  className={`trash-view__pagination-btn ${
                    currentPage === totalPages
                      ? 'trash-view__pagination-btn--disabled'
                      : 'trash-view__pagination-btn--next'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Summary when no pagination needed */}
      {totalPages <= 1 && totalModels > 0 && (
        <div className="trash-view__summary">
          Showing all {totalModels} deleted models
        </div>
      )}
      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmAction?.type === 'restore' ? 'Restore model?' : confirmAction?.type === 'hardDelete' ? 'Delete model permanently?' : 'Empty trash?'}
        message={confirmAction?.type === 'empty'
          ? 'This will permanently delete all models in trash. This action cannot be undone.'
          : confirmAction?.type === 'hardDelete'
            ? 'This action cannot be undone.'
            : 'The model will be restored to the registry.'}
        confirmLabel={confirmAction?.type === 'restore' ? 'Restore' : 'Delete'}
        onConfirm={() => {
          if (confirmAction?.type === 'restore') return handleRestoreModel(confirmAction.modelId);
          if (confirmAction?.type === 'hardDelete') return handleHardDeleteModel(confirmAction.modelId);
          return handleEmptyTrash();
        }}
        onCancel={() => setConfirmAction(null)}
        loading={actionLoading}
      />
    </div>
  );
};

export default TrashView;

